import React from 'react';
import PropTypes from 'prop-types';
import * as styles from './Card.module.css';

/**
 * Card - A reusable card component for displaying content with optional actions
 * 
 * @example
 * // Simple card
 * <Card title="Report Name">
 *   Card content goes here
 * </Card>
 * 
 * @example
 * // Card with icon and badge
 * <Card
 *   title="Sales Report"
 *   icon={<ChartIcon />}
 *   badge={<StatusBadge status="published" />}
 *   onClick={() => console.log('clicked')}
 * >
 *   Report description
 * </Card>
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Card content
 * @param {string} props.title - Card title
 * @param {string} props.subtitle - Card subtitle
 * @param {React.ReactNode} props.icon - Icon element to display
 * @param {React.ReactNode} props.badge - Badge element (e.g., status badge)
 * @param {React.ReactNode} props.actions - Action buttons/menu
 * @param {React.ReactNode} props.footer - Footer content
 * @param {Function} props.onClick - Click handler
 * @param {Function} props.onKeyDown - Key down handler
 * @param {string} props.className - Additional CSS class
 * @param {boolean} props.selected - Whether card is selected
 * @param {string} props.variant - Card variant ('default', 'elevated', 'outlined')
 * @param {string} props.status - Status for styling (e.g., 'draft', 'published')
 * @param {boolean} props.hoverable - Whether card has hover effect
 * @param {Object} props.dataAttributes - Additional data attributes
 */
function Card({
  children,
  title,
  subtitle,
  icon,
  badge,
  actions,
  footer,
  onClick,
  onKeyDown,
  className,
  selected,
  variant,
  status,
  hoverable,
  dataAttributes,
}) {
  const handleKeyDown = (e) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick(e);
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  // Auto-enable hoverable if onClick is provided (unless explicitly disabled)
  const isHoverable = hoverable !== false && (hoverable === true || onClick);
  
  const cardClasses = [
    styles.card,
    variant && styles[variant],
    selected && styles.selected,
    isHoverable && styles.hoverable,
    className,
  ].filter(Boolean).join(' ');

  const interactive = Boolean(onClick);
  
  const cardProps = interactive
    ? {
        onClick,
        onKeyDown: handleKeyDown,
        role: 'button',
        tabIndex: 0,
      }
    : {};

  return (
    <div
      className={cardClasses}
      {...cardProps}
      data-status={status}
      aria-label={interactive && !title ? 'Card' : undefined}
      {...dataAttributes}
    >
      {(title || icon || badge || actions) && (
        <div className={styles.header}>
          {icon && <div className={styles.iconContainer}>{icon}</div>}
          
          <div className={styles.headerContent}>
            {(title || badge) && (
              <div className={styles.titleRow}>
                {title && <h3 className={styles.title}>{title}</h3>}
                {actions && <div className={styles.actions}>{actions}</div>}
              </div>
            )}
            {(subtitle || badge) && (
              <div className={styles.metaRow}>
                {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
                {badge && <div className={styles.badge}>{badge}</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {children && <div className={styles.content}>{children}</div>}

      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  );
}

Card.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  icon: PropTypes.node,
  badge: PropTypes.node,
  actions: PropTypes.node,
  footer: PropTypes.node,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  className: PropTypes.string,
  selected: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'elevated', 'outlined']),
  status: PropTypes.string,
  hoverable: PropTypes.bool,
  dataAttributes: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

Card.defaultProps = {
  children: null,
  title: null,
  subtitle: null,
  icon: null,
  badge: null,
  actions: null,
  footer: null,
  onClick: null,
  onKeyDown: null,
  className: '',
  selected: false,
  variant: 'default',
  status: null,
  hoverable: true,
  dataAttributes: {},
};

export default Card;

